import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { useCheckPiiMutation } from "../../../src/taskpane/components/_v2/redux/api/local/api-pii-slice";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { extensions, OneNoteProgId, piiConfig, SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { gridSelectedItems } from "../ContentManagerBody";
import { GridProps } from "../useContentManagerBody";


export function useMenuItem_PII(selectedItems: any[], waitFor: boolean, gridProps: GridProps, setGridProps: (gridProps:GridProps) => void, setSelectedItems: (selectedItems: gridSelectedItems) => void) {
    const languageStrings = React.useContext(LanguageContext);
    
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const tenantEnableRedaction = useSelector((state: RootState) => state.user.tenantEnableRedaction);
    const tenantEnablePII = useSelector((state: RootState) => state.user.tenantEnablePII);
    
    const [menuItem_PII, setMenuItem_PII] = React.useState<IContextualMenuItem>();

    const [checkPiiMutation] = useCheckPiiMutation();

    React.useEffect(() => {
      if (selectedItems?.length > 0 && tenantEnablePII && currentDirectory.type !== ComponentTypes.SharePointList &&
          !(selectedItems.every(x => x?.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder))) &&
          !(selectedItems.some(x => x?.ProgId === OneNoteProgId)) &&
          selectedItems.some(item => item && item[piiConfig.privacySentitiveColumnName] !== undefined) &&
          selectedItems.some(item => item && item[piiConfig.privacySentitiveDataColumnName] !== undefined) &&
          !selectedItems.some(x => x?.EffectiveBasePermissions?.canEditListItem === false)) {

          const piiItem: IContextualMenuItem = {
            key: "privacy",
            disabled: waitFor === true,
            text: languageStrings.PrivacySensitiveDisplay,
            iconProps: { iconName: "Unlock" },
            subMenuProps: {
              items: [
                {
                  key: "link",
                  text: languageStrings.Recheck,
                  iconProps: { iconName: "Refresh" },
                  onClick: () => {
                    recheckPii(false);
                  }
                }
              ]
            }
          }

          if (tenantEnableRedaction) {
            let showRedactButton = false;
            selectedItems?.forEach(selectedFile => {
              if (
                selectedFile.File_x0020_Type === extensions.pdf &&
                selectedFile[piiConfig.privacySentitiveDataColumnName] !== undefined &&
                selectedFile[piiConfig.privacySentitiveColumnName] === 'Yes'
              ) {
                showRedactButton = true;
              }
            });

            if (showRedactButton) {

              piiItem.subMenuProps.items.push({
                key: "redact",
                text: languageStrings.Redact,
                iconProps: { iconName: "PageEdit" },
                onClick: () => {
                  recheckPii(true);
                }
              });
            }
          }

          setMenuItem_PII(piiItem);
        } else {
          setMenuItem_PII(null);
        }
    }, [selectedItems, waitFor, currentDirectory]);

    const recheckPii = async (redactPiiData: boolean) => {
      const newGridProps: GridProps = { ...gridProps };
      let newSelectedFiles: any[] = [...selectedItems];
  
      let newRows: any[] = []
      try {
        newGridProps.items.forEach(item => {
          const newItem = item ? { ...item } : item;
  
          const isSelected = item && newSelectedFiles.find(x => x.ID === item.ID);
  
          if ((isSelected && isSelected.FSObjType === '0' && !redactPiiData) || (isSelected && isSelected.FSObjType === '0' && redactPiiData && isSelected.File_x0020_Type === extensions.pdf && isSelected[piiConfig.privacySentitiveColumnName] === 'Yes')) {
            newItem[piiConfig.privacySentitiveColumnName] = "No";
            newItem[piiConfig.privacySentitiveDataColumnName] = "";
            newItem['isBusy'] = true;
          }
          newRows.push(newItem);
        });
  
        newGridProps.items = [...newRows];
  
        setGridProps(newGridProps);
  
        const domain = `https://${currentDirectory.webUrl.split('//')[1].split('/')[0]}`;
  
        await Promise.all(newGridProps.items.map(async (item) => {
          const isSelected = { ...newSelectedFiles.find(x => x.ID === item.ID) };
  
          if ((isSelected && isSelected.FSObjType === '0' && !redactPiiData) || (isSelected && isSelected.FSObjType === '0' && redactPiiData && isSelected.File_x0020_Type === extensions.pdf && isSelected[piiConfig.privacySentitiveColumnName] === 'Yes')) {
            try {
              const piiResults = await checkPiiMutation({
                accessToken: await authModule.getSharePointAccessToken(),
                fileUrl: `${domain}${item['FileRef']}`,
                siteUrl: currentDirectory.webUrl,
                forceRecheck: true,
                redactPiiData
              }).unwrap();
  
              try {
                item[piiConfig.privacySentitiveColumnName] = piiResults.piiDetected ? "Yes" : "No";
                isSelected[piiConfig.privacySentitiveColumnName] = piiResults.piiDetected ? "Yes" : "No";
  
                if (piiResults.piiError) {
                  item[piiConfig.privacySentitiveDataColumnName] = languageStrings.PrivacySensitiveCheckError
                  isSelected[piiConfig.privacySentitiveDataColumnName] = languageStrings.PrivacySensitiveCheckError
                } else {
                  item[piiConfig.privacySentitiveDataColumnName] = piiResults.pii;
                  isSelected[piiConfig.privacySentitiveDataColumnName] = piiResults.pii;
                }
  
                item['isBusy'] = false;
  
                setGridProps({ ...newGridProps, items: [...newGridProps.items] });
  
                const newSelectedItems: any[] = [];
                newSelectedFiles.forEach(selectedFile => {
                  if (selectedFile.ID === isSelected.ID) {
                    newSelectedItems.push({ ...isSelected });
                  }
                  else {
                    newSelectedItems.push({ ...selectedFile });
                  }
                });
  
  
                newSelectedFiles = [...newSelectedItems];
                setSelectedItems({ ...selectedItems, items: [...newSelectedFiles], count: selectedItems.length });
  
              } catch (error) {
                logger(error, "useMenuItem_PII.tsx (recheckPii promise map)");
              }
 
            } catch (error) {
              item[piiConfig.privacySentitiveColumnName] = "No";
              item[piiConfig.privacySentitiveDataColumnName] = languageStrings.PrivacySensitiveCheckError;
              item['isBusy'] = false;
  
              setGridProps({ ...newGridProps, items: [...newGridProps.items] });
            }
          }
        }));
  
      } catch (error) {
        logger(error, "useMenuItem_PII.tsx (recheckPii)");
      }
    }
    
    return {
      menuItem_PII
    }

}